import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import CinematicBackground from "@/components/CinematicBackground";
import { PageHeader } from "@/pages/SceneAnalysis";
import { buildFilterCSS } from "@/data/cinema";
import { TEMPLATE_IMAGES } from "@/constants/images";
import { useApp } from "@/context/AppContext";
import { useLang, useCinema } from "@/i18n/LanguageContext";
import { toast } from "@/components/ui/sonner";

export default function Templates() {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { TEMPLATES, FILTER_BY_ID } = useCinema();
  const { setActiveTemplateId, setActiveFilterId } = useApp();
  const [openId, setOpenId] = useState(null);
  const rtl = lang === "ar";

  const applyTemplate = (tpl) => {
    setActiveTemplateId(tpl.id);
    setActiveFilterId(tpl.filter);
    toast.success(`${t("t_templateApplied")}: ${tpl.title.split("–")[0].trim()}`);
    navigate("/app/camera");
  };

  return (
    <div className="relative px-4 pt-7" dir={rtl ? "rtl" : "ltr"} data-testid="templates-page">
      <CinematicBackground />
      <PageHeader title={t("templates_title")} icon="fa-clapperboard" />
      <p className="text-xs text-slate-400 mb-5 bg-white/5 rounded-xl p-3 leading-relaxed">
        <i className="fa-solid fa-circle-info text-ai mx-1" />
        {t("templates_note")}
      </p>

      <div className="space-y-4">
        {TEMPLATES.map((tpl, i) => {
          const open = openId === tpl.id;
          const css = buildFilterCSS({ ...FILTER_BY_ID[tpl.filter].base, intensity: 75 });
          return (
            <motion.div key={tpl.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              className="glass rounded-3xl overflow-hidden" style={{ borderColor: `${tpl.accent}44` }} data-testid={`template-${tpl.id}`}>
              <div className="relative h-36">
                <img src={TEMPLATE_IMAGES[tpl.id]} alt={tpl.title} className="w-full h-full object-cover" style={{ filter: css }} />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(4,6,13,0.95), transparent 60%)" }} />
                <span className="absolute top-3 right-3 text-[10px] rounded-full px-2 py-0.5" style={{ background: `${tpl.accent}33`, color: tpl.accent, border: `1px solid ${tpl.accent}66` }}>{tpl.tag}</span>
                <h3 className="absolute bottom-3 right-3 left-3 font-display text-lg text-white">{tpl.title}</h3>
              </div>

              <div className="p-4">
                <p className="text-sm text-slate-300 leading-relaxed mb-3">{tpl.style}</p>

                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs text-slate-400">{t("colorPalette")}:</span>
                  {tpl.palette.map((c) => <span key={c} className="w-6 h-6 rounded-md border border-white/15" style={{ background: c }} />)}
                </div>

                <button onClick={() => setOpenId(open ? null : tpl.id)} data-testid={`template-details-${tpl.id}`}
                  className="w-full glass rounded-xl py-2.5 text-sm text-slate-200 flex items-center justify-center gap-2 mb-3">
                  <i className={`fa-solid fa-chevron-${open ? "up" : "down"}`} /> {open ? t("hideDetails") : t("showDetails")}
                </button>

                <AnimatePresence>
                  {open && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden space-y-3">
                      <SpecGrid tpl={tpl} t={t} filterName={FILTER_BY_ID[tpl.filter].name} />
                      <LightPlan accent={tpl.accent} plan={tpl.lightPlan} t={t} />
                      <div className="bg-white/5 rounded-xl p-3">
                        <p className="text-xs text-gold mb-1 flex items-center gap-2"><i className="fa-solid fa-dumbbell" /> {t("exercise")}</p>
                        <p className="text-sm text-slate-200 leading-relaxed">{tpl.exercise}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <button onClick={() => applyTemplate(tpl)} data-testid={`apply-template-${tpl.id}`}
                  className="btn-gold w-full rounded-xl py-3 font-display mt-3 flex items-center justify-center gap-2">
                  <i className="fa-solid fa-vr-cardboard" /> {t("applyToCamera")}
                </button>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

function SpecGrid({ tpl, t, filterName }) {
  const rows = [
    [t("lightingType"), tpl.lighting], [t("lens"), tpl.lens], [t("focal"), tpl.focal],
    [t("angle"), tpl.angle], [t("shotSize"), tpl.shot], [t("cameraMovement"), tpl.movement],
    [t("suggestedFilter"), filterName],
  ];
  return (
    <div className="grid grid-cols-2 gap-2">
      {rows.map(([k, v]) => (
        <div key={k} className="bg-white/5 rounded-lg p-2">
          <p className="text-[10px] text-slate-400">{k}</p>
          <p className="text-xs text-slate-100">{v}</p>
        </div>
      ))}
      <div className="col-span-2 bg-white/5 rounded-lg p-2">
        <p className="text-[10px] text-slate-400 mb-1">{t("suggestedSettings")}</p>
        <div className="flex flex-wrap gap-2 text-[11px]">
          {Object.entries(tpl.settings).map(([k, v]) => (
            <span key={k} className="font-display rounded-full px-2 py-0.5 border border-gold/30 text-gold">{k.toUpperCase()}: {v}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function LightPlan({ accent, plan, t }) {
  return (
    <div className="bg-white/5 rounded-xl p-3">
      <p className="text-xs text-ai mb-2 flex items-center gap-2"><i className="fa-solid fa-diagram-project" /> {t("lightPlan")}</p>
      <div className="flex items-center gap-3">
        <svg width="120" height="80" className="shrink-0">
          <ellipse cx="60" cy="70" rx="45" ry="8" fill="rgba(255,255,255,0.05)" />
          <circle cx="60" cy="45" r="10" fill={accent} opacity="0.9" />
          <text x="60" y="48" textAnchor="middle" fontSize="7" fill="#04060d">S</text>
          <circle cx="18" cy="20" r="7" fill="#d9b46a" />
          <line x1="24" y1="24" x2="52" y2="40" stroke="#d9b46a" strokeWidth="1.5" strokeDasharray="3 2" />
          <circle cx="102" cy="18" r="6" fill="#4ea8ff" />
          <line x1="97" y1="22" x2="68" y2="40" stroke="#4ea8ff" strokeWidth="1.5" strokeDasharray="3 2" />
          <rect x="52" y="72" width="16" height="6" rx="1" fill="#94a3b8" />
        </svg>
        <p className="text-[11px] text-slate-300 leading-relaxed">{plan}</p>
      </div>
    </div>
  );
}
